import { useCallback, useEffect, useState } from "react";
import {
  AccountState,
  ConvergenceMatrix,
  RiskState,
  api,
  isError,
} from "../api";
import { useBridgePoll } from "./useBridgePoll";

const POLL_MS = 2000;

export interface OverviewState {
  bridgeConnected: boolean;
  account: AccountState | null;
  accountError: string | null;
  risk: RiskState | null;
  riskError: string | null;
  matrix: ConvergenceMatrix | null;
  forceRefresh: () => void;
  lastTick: Date | null;
}

/** Bundles polling for the overview page (account / risk / convergence
 * matrix). Each tick fires the three requests in parallel; bridge state
 * comes from the global hook so we don't double-poll it. */
export function useOverviewPoll(): OverviewState {
  const [account, setAccount] = useState<AccountState | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [risk, setRisk] = useState<RiskState | null>(null);
  const [riskError, setRiskError] = useState<string | null>(null);
  const [matrix, setMatrix] = useState<ConvergenceMatrix | null>(null);
  const [pollNonce, setPollNonce] = useState(0);
  const [lastTick, setLastTick] = useState<Date | null>(null);
  const bridge = useBridgePoll();

  const tick = useCallback(async () => {
    const [s, rs, mx] = await Promise.all([
      api.accountState(),
      api.riskState(),
      api.convergenceMatrix(),
    ]);
    if (isError(s)) {
      setAccount(null);
      setAccountError(s.error);
    } else {
      setAccount(s);
      setAccountError(null);
    }
    if (isError(rs)) {
      setRisk(null);
      setRiskError(rs.error);
    } else {
      setRisk(rs);
      setRiskError(null);
    }
    setMatrix(isError(mx) ? null : mx);
    setLastTick(new Date());
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await tick();
    };
    void run();
    const id = setInterval(run, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [tick, pollNonce]);

  const forceRefresh = useCallback(() => setPollNonce((n) => n + 1), []);

  return {
    bridgeConnected: !!bridge?.connected,
    account,
    accountError,
    risk,
    riskError,
    matrix,
    forceRefresh,
    lastTick,
  };
}
